'use client';

import { Language, Subsidiary } from '@coolblue-development/constants';
import { Flex, Typography } from '@coolblue-development/freddy';
import { IncludedByDefault } from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import { ProductPlusBlock } from '@features/product/components/ProductPlus/ProductPlusBlock';
import type { MappedPromotionProductUrl } from '@features/product/components/ProductPlus/types';
import type { Product } from '@features/product/typings';
import { MainProduct } from '@features/second-chance/typings';
import { useTranslation } from '@i18n/useTranslation';
import { Fragment, ReactElement, useMemo } from 'react';

enum PromotionType {
  Norton = 'norton',
  FreeWith = 'freewith'
}

const IncludedByDefaultSection = ({
  product,
  mappedPromotionProductUrls,
  isSecondChance = false
}: {
  product: Product | MainProduct;
  mappedPromotionProductUrls?: MappedPromotionProductUrl[];
  isSecondChance?: boolean;
}) => {
  const { t } = useTranslation('webshop.productPage.productInformation');
  const { subsidiary, language } = useRequestContext();

  const isComposedProduct = product.subProducts?.length > 1;

  const includedByDefaultSectionTitle = useMemo(() => {
    if (subsidiary === Subsidiary.NL && language === Language.DUTCH) {
      return t('includedByDefault.nl_nl');
    }

    if (subsidiary === Subsidiary.BE) {
      if (language === Language.ENGLISH) {
        return t('includedByDefault.be_en');
      } else if (language === Language.FRENCH) {
        return t('includedByDefault.be_fr');
      }
    }

    return t('includedByDefault.default');
  }, [language, subsidiary, t]);

  const getIncludedByDefaultWithPromotions = (
    item: Pick<Product, 'includedInBox' | 'manual' | 'productPlusPromotions'>
  ) => {
    const hasNortonPromotion =
      item.productPlusPromotions?.[0]?.type.toLowerCase() === PromotionType.Norton;
    const hasFreeWithPromotion =
      item.productPlusPromotions?.[0]?.type.toLowerCase() === PromotionType.FreeWith &&
      !item.productPlusPromotions?.[0].consent;

    const includedInBox = (item.includedInBox ?? []).filter(
      includedInBox => includedInBox !== null
    );

    if (!includedInBox.length && !item.productPlusPromotions?.length) return null;

    return (
      <IncludedByDefault
        manualUrl={item.manual?.manualUrl}
        manualSizeFile={item.manual?.fileSizeBytes ?? undefined}
        includedItems={includedInBox}
        {...(item.productPlusPromotions &&
          (hasNortonPromotion || hasFreeWithPromotion) && {
            promotionInfoUsp: (
              <ProductPlusBlock
                promotions={item.productPlusPromotions ?? []}
                mappedPromotionProductUrls={mappedPromotionProductUrls}
                hideInfoUspIcon={true}
                disableShowTracking={true}
              />
            )
          })}
      />
    );
  };

  const getIncludedByDefault = (item: Pick<Product, 'includedInBox' | 'manual'>) => {
    const includedInBox = (item.includedInBox ?? []).filter(
      includedInBox => includedInBox !== null
    );

    if (!includedInBox.length) return null;

    return (
      <IncludedByDefault
        manualUrl={item.manual?.manualUrl}
        manualSizeFile={item.manual?.fileSizeBytes ?? undefined}
        includedItems={includedInBox}
      />
    );
  };

  const mainProductData = isSecondChance
    ? getIncludedByDefault(product)
    : getIncludedByDefaultWithPromotions(product);
  let composedProductData = null;

  if (isComposedProduct) {
    const subProductsBlocks: ReactElement[] = [];

    product.subProducts.forEach(subProduct => {
      const subProductData = subProduct?.product ? getIncludedByDefault(subProduct.product) : null;

      if (subProductData && subProduct?.product?.name) {
        subProductsBlocks.push(
          <Flex direction='column' gap={100}>
            <Typography variant='h4' m={0}>
              {subProduct.product.name}
            </Typography>
            {subProductData}
          </Flex>
        );
      }
    });

    if (subProductsBlocks.length) {
      composedProductData = (
        <Flex direction='column' gap={200}>
          {subProductsBlocks.map((block, index) => (
            <Fragment key={index}>{block}</Fragment>
          ))}
        </Flex>
      );
    }
  }

  if (!mainProductData && !composedProductData) {
    return null;
  }

  return (
    <Flex component='section' direction='column' gap={200}>
      <Typography variant='h3' m={0}>
        {includedByDefaultSectionTitle}
      </Typography>
      {composedProductData || mainProductData}
    </Flex>
  );
};

export default IncludedByDefaultSection;
